import { useEffect, useId, useRef } from "react";
import type { TradingActionId } from "@helm/ui";

/**
 * Trading hotkey targets (SPEC §9.10): a DOM ladder or order pad registers what B S J A R F C X
 * mean for it (its symbol, account and quantity) under an id it puts on its root element as
 * `data-trading-ctx`. The shell's key handler runs the innermost focused target's handler; with
 * no target in focus (e.g. a chart — chart trading is not wired yet) the key does nothing.
 */
export type HotkeyHandler = (action: TradingActionId, origin: Element) => void;

const targets = new Map<string, { current: HotkeyHandler }>();

export function hotkeyTargetOf(el: Element | null): HotkeyHandler | null {
  const host = el?.closest("[data-trading-ctx]");
  const id = host?.getAttribute("data-trading-ctx");
  if (!id) return null;
  const t = targets.get(id);
  return t ? t.current : null;
}

/** Register `handler` (latest render wins); returns the id for `data-trading-ctx`. */
export function useHotkeyTarget(handler: HotkeyHandler): string {
  const id = useId();
  const ref = useRef(handler);
  ref.current = handler;
  useEffect(() => {
    targets.set(id, ref);
    return () => {
      targets.delete(id);
    };
  }, [id]);
  return id;
}
